"use client";

import { Icon, OspToast, ospThemes } from "@ospweb/sdk-ui";
import {
  MemberProductLevelEnum,
  MemberStatusEnum,
  Opportunity,
  OpportunityTypeEnum,
} from "@/src/features/api/memberApi";
import {
  useAppPurchaseFlow,
  useMemberFeature,
  useMemberUser,
  useShellRouter,
} from "@/src/hooks";
import {
  ProductPayCycle,
  ProductStatus,
  ProductType,
} from "@/src/components/Member/CurrentPlan/constants";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  opportunitiesQueryKeys,
  useOpportunitySummary,
} from "@/src/hooks/useOpportunities";

import { I18nUtil } from "@utils/i18n";
import NextImage, { AppImage } from "@/src/components/NextImage";
import { IpoEnum } from "../types";
import { MemberKeys } from "@/src/screens/Member";
import { MessageType } from "@/src/lib/bridge/types";
import { QUERY_KEYS } from "@/src/config/constant";
import { ROUTER_PATH } from "@/src/config/router";
import { SocialProofItem } from "./SocialProofItem";
import Translations from "@translate/components/AlphaPick/AlphaPickPricing/PaywallModalContent/PaywallModalContent";
import { agreement } from "@/src/const/agreement";
import { cn } from "utils";
import log from "logbeacon";
import { gaTrackingAttrs, trackEvent } from "@/src/utils/tracking";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { useQueryClient } from "@tanstack/react-query";
import { EdgenModalCreateAble } from "@components/EdgenModal";

const I18n = I18nUtil.createScoped(Translations);

const PAYWALL_HERO_BG = "/assets/images/paywall-hero-bg.webp";

interface PaywallFeatureItem {
  text: string;
}

export interface PaywallSocialProof {
  user: string;
  actionText: string;
  timeAgo: string;
}

interface PaywallCountdown {
  time: { hh: string; mm: string; ss: string };
}

export interface PaywallModalContentProps {
  /** 倒计时初始值（可选，未传则默认 24:00:00） */
  countdown?: PaywallCountdown;
  /** 点击关闭（可选） */
  onClose?: () => void;
  /** PC 模式（影响 padding/圆角/布局） */
  isPc?: boolean;
  routerType?: string;
  opportunity?: Opportunity;
  credits?: number;
  trackingModuleName?: string;
  trackingSymbol?: string;
  paywallCopyOverrides?: {
    title?: string;
    subtitle?: string;
    features?: string[];
    hideSocialProof?: boolean;
    hideOfferCountdown?: boolean;
  };
  /** 为 true 时不渲染内容区 close Icon（PC / H5 通用） */
  hideCloseButton?: boolean;
  /** 为 true 时在主 CTA 下展示「Just Look around」 */
  showJustLookAroundButton?: boolean;
  /** 次要按钮点击；未传时等同 onClose */
  onJustLookAroundClick?: () => void;
  /**
   * 弹窗内**导航类**动作（跳 `/member` 定价页）触发的关闭动作；未传时回退 onClose。
   * 与 onClose 的区别：不执行调用方的 dismiss 副作用（如 `router.push` 回列表页），
   * 避免同 tick 两次导航（详见 `../types.ts`）。
   */
  onNavigateAway?: () => void;
  /** Hero 背景图；默认通用 paywall-hero-bg */
  heroBackgroundSrc?: string;
}

export const PAYWALL_DAILY_COUNTDOWN_KEY = "paywall_daily_countdown_v1";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const getDayKey = (date = new Date()): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatRemainTime = (remainMs: number): PaywallCountdown["time"] => {
  const safeMs = Math.max(0, remainMs);
  const totalSeconds = Math.floor(safeMs / 1000);
  const hh = Math.floor(totalSeconds / 3600);
  const mm = Math.floor((totalSeconds % 3600) / 60);
  const ss = totalSeconds % 60;

  return {
    hh: `${hh}`.padStart(2, "0"),
    mm: `${mm}`.padStart(2, "0"),
    ss: `${ss}`.padStart(2, "0"),
  };
};

type DailyCountdownCache = {
  dayKey: string;
  startTs: number;
};

const resolveDailyCountdownRemainMs = (nowTs: number): number => {
  const now = new Date(nowTs);
  const dayKey = getDayKey(now);
  let startTs = nowTs;

  try {
    const raw = localStorage.getItem(PAYWALL_DAILY_COUNTDOWN_KEY);
    if (raw) {
      const cache = JSON.parse(raw) as DailyCountdownCache;
      if (cache?.dayKey === dayKey && typeof cache?.startTs === "number") {
        startTs = cache.startTs;
      }
    }
  } catch {
    // ignore invalid cache and re-init below
  }

  if (startTs === nowTs) {
    const nextCache: DailyCountdownCache = { dayKey, startTs };
    localStorage.setItem(
      PAYWALL_DAILY_COUNTDOWN_KEY,
      JSON.stringify(nextCache),
    );
  }

  const endTs = startTs + ONE_DAY_MS;
  return Math.max(0, endTs - nowTs);
};

const getDefaultFeatures = (): PaywallFeatureItem[] => {
  return [
    { text: I18n.t("Monitors your portfolio while you're away") },
    { text: I18n.t("Flag risks before they land") },
    { text: I18n.t("Answers built on your unique history") },
  ];
};

const getFeaturesByOverrides = (
  featureTexts?: string[],
): PaywallFeatureItem[] => {
  if (!featureTexts?.length) {
    return getDefaultFeatures();
  }
  return featureTexts.slice(0, 3).map((text) => ({ text }));
};

const EdCompleteAnnualCard = ({
  annualPrice,
  className,
}: {
  annualPrice?: number;
  className?: string;
}) => (
  <div
    className={cn(
      "rounded-2 border-2 border-border_accent4_solid bg-background_accent4_overlay10 p-3 w-full",
      className,
    )}
  >
    <p className="title_m text-content_primary">
      {I18n.t("Ed Complete Annual")}
    </p>
    <div className="mt-3 flex flex-wrap items-end gap-1">
      <span className="headline_regular_l text-content_accent4_primary">
        ${annualPrice != null ? annualPrice.toFixed(2) : "--"}
      </span>
      {annualPrice != null && (
        <span className="body_s text-content_secondary">
          {I18n.t("(≈$%{var0}/mo)", {
            var0: (annualPrice / 12).toFixed(2),
          })}
        </span>
      )}
    </div>
  </div>
);

export const PaywallModalContent: React.FC<PaywallModalContentProps> = ({
  countdown,
  onClose,
  isPc = false,
  opportunity,
  credits,
  routerType,
  trackingModuleName,
  trackingSymbol,
  paywallCopyOverrides,
  hideCloseButton = false,
  showJustLookAroundButton = false,
  onJustLookAroundClick,
  onNavigateAway,
  heroBackgroundSrc = PAYWALL_HERO_BG,
}) => {
  const queryClient = useQueryClient();
  const { data: memberProductData, trialList } = useMemberFeature();
  const { refetch: refetchMemberUser } = useMemberUser();
  const { purchaseInApp, shouldTryNativePayment } = useAppPurchaseFlow();
  const { send, isInApp } = useBridge();
  const router = useShellRouter();
  const opportunityId = opportunity?.id || "";
  const moduleName = IpoEnum[opportunity?.type || OpportunityTypeEnum.IPO];
  const isCustomCopyMode = Boolean(
    paywallCopyOverrides?.title || paywallCopyOverrides?.subtitle,
  );
  const hideOfferCountdown = Boolean(paywallCopyOverrides?.hideOfferCountdown);
  const { data: summaryData } = useOpportunitySummary({
    params: {
      types: [
        OpportunityTypeEnum.AI_PICK,
        OpportunityTypeEnum.EARNINGS,
        OpportunityTypeEnum.IPO,
      ],
    },
  });
  const aiPickSummary = summaryData?.find(
    (item) => item.type === OpportunityTypeEnum.AI_PICK,
  );
  const chartData = {
    total_returns: aiPickSummary?.total_returns || [],
    index_returns: aiPickSummary?.index_returns || [],
  };
  const edgenReturn = chartData?.total_returns?.length
    ? (
        Number(
          aiPickSummary?.total_returns?.[aiPickSummary.total_returns.length - 1]
            ?.price,
        ) * 100
      ).toFixed(2)
    : "0";
  const edgenReturnText = `${Number(edgenReturn) > 0 ? "+" : ""}${edgenReturn}%`;
  const featureItems = useMemo(
    () => getFeaturesByOverrides(paywallCopyOverrides?.features),
    [paywallCopyOverrides?.features],
  );
  const [isAppPayingUnlockLoading, setIsAppPayingUnlockLoading] =
    useState(false);

  const openExternalUrl = useCallback(
    (url: string) => {
      if (isInApp) {
        send(MessageType.OPEN_URL_EXTERNAL, { url });
        return;
      }
      window.open(url, "_blank");
    },
    [isInApp, send],
  );

  const handleTermsClick = () => {
    openExternalUrl(agreement.termsLink);
  };

  const handlePrivacyClick = () => {
    openExternalUrl(agreement.privacyLink);
  };

  /**
   * 跳定价页：这是**导航**而非 dismiss，所以只关弹窗（`onNavigateAway`），不能走 `onClose`。
   * 调用方（如 `AlphaPickDetailPage`）的 `onClose` 自带 `router.push` 回列表页，与这里的 push
   * 形成同 tick 双导航：浏览器落点不确定；App 内更严重 —— `/invest` 命中壳 Tab 路径走
   * `popDetailAndNavigateShell`（原生收走 Detail），`/member` 则在同一 Detail WebView 内
   * `router.push`，被保活复用后就表现为「点股票直接进定价页」。
   */
  const goToPricingPage = useCallback(() => {
    (onNavigateAway ?? onClose)?.();
    router.push(`${ROUTER_PATH.MEMBER}/?tab=${MemberKeys.PRICING}`);
  }, [onNavigateAway, onClose, router]);

  const onUnlockSuccess = useCallback(async () => {
    trackEvent("ob_funnel_trial_started", {
      subscription_id: opportunity?.id ?? "",
    });
    if (shouldTryNativePayment) {
      const maxAttempts = 5;
      const intervalMs = 1000;
      let isActive = false;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const result = await refetchMemberUser();
        const status = result?.data?.status;
        if (status === MemberStatusEnum.ACTIVE) {
          isActive = true;
          break;
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }
      if (!isActive) {
        goToPricingPage();
        return;
      }
    }
    // onClose?.();
    // 成功后关闭弹窗：返回 true 表示支付成功，并确认会员权益发放
    EdgenModalCreateAble.end(true);
    await queryClient.invalidateQueries({
      queryKey: [...opportunitiesQueryKeys.all, "list"],
    });
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
    });
    routerType && router.push(`${routerType}/${opportunityId}`);
  }, [
    refetchMemberUser,
    goToPricingPage,
    router,
    opportunityId,
    queryClient,
    routerType,
    shouldTryNativePayment,
  ]);

  const completeYearDisplayProduct = useMemo(() => {
    const yearProducts = (memberProductData || []).filter(
      (item) =>
        item.pay_cycle === ProductPayCycle.YEAR &&
        item.status === ProductStatus.ACTIVE &&
        item.type === ProductType.NORMAL &&
        item.level === MemberProductLevelEnum.EXPERT,
    );
    return yearProducts.length > 0 ? yearProducts[0] : undefined;
  }, [memberProductData]);

  const completeYearProduct = useMemo(() => {
    const trialProduct = trialList?.find(
      (item) => item.renewal_id === completeYearDisplayProduct?.renewal_id,
    );
    if (trialProduct) {
      return trialProduct;
    }
    return completeYearDisplayProduct;
  }, [completeYearDisplayProduct, trialList]);

  const annualDisplayPrice = Number(
    completeYearDisplayProduct?.price ?? completeYearProduct?.price ?? 0,
  );

  const handleUnlockCardClick = useCallback(async () => {
    const moduleNameForTracking = trackingModuleName || moduleName;
    const symbolForTracking =
      trackingSymbol || opportunity?.asset?.symbol || "";

    trackEvent("ob_paywall_trial_click", {
      subscription_id: opportunity?.id ?? "",
    });

    log.trace("[GTM_CLICK][spend_membership]", {
      module_name: moduleNameForTracking,
      trigger_symbol: symbolForTracking,
      current_balance: credits,
    });
    if (shouldTryNativePayment) {
      if (!completeYearProduct) {
        goToPricingPage();
        return;
      }
      setIsAppPayingUnlockLoading(true);
      const result = await purchaseInApp(completeYearProduct, {
        onSuccess: () => {
          onUnlockSuccess().finally(() => {
            setIsAppPayingUnlockLoading(false);
          });
        },
      });
      if (!result.success) {
        setIsAppPayingUnlockLoading(false);
        if (result.fallbackToStripe) {
          goToPricingPage();
        } else {
          OspToast.error({
            content: result.error ?? I18n.t("Purchase failed."),
          });
        }
      }
    } else {
      goToPricingPage();
    }
  }, [
    shouldTryNativePayment,
    onUnlockSuccess,
    goToPricingPage,
    moduleName,
    trackingModuleName,
    trackingSymbol,
    opportunity,
    credits,
    completeYearProduct,
    purchaseInApp,
  ]);

  const [dailyCountdownTime, setDailyCountdownTime] = useState<
    PaywallCountdown["time"]
  >(() => {
    return countdown?.time ?? { hh: "24", mm: "00", ss: "00" };
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateCountdown = () => {
      const remainMs = resolveDailyCountdownRemainMs(Date.now());
      setDailyCountdownTime(formatRemainTime(remainMs));
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const isBuyTrialNow = completeYearProduct?.type === ProductType.TRIAL;

  const buttonText = useMemo(() => {
    if (isBuyTrialNow) {
      if (completeYearProduct?.price && Number(completeYearProduct.price) > 0) {
        return I18n.t("Try 7 Days for Just $%{var0}", {
          var0: Number(completeYearProduct.price).toFixed(2),
        });
      }
      return I18n.t("Start Your 7-Day Free Trial");
    }
    return I18n.t("Subscribe now");
  }, [completeYearProduct?.price, isBuyTrialNow]);

  const handleJustLookAroundClick = useCallback(() => {
    trackEvent("ob_paywall_skip_click");
    if (onJustLookAroundClick) {
      onJustLookAroundClick();
      return;
    }
    onClose?.();
  }, [onClose, onJustLookAroundClick]);

  const showSocialProof =
    isBuyTrialNow && !paywallCopyOverrides?.hideSocialProof;

  const renderHeroTitle = () => {
    if (isCustomCopyMode) {
      return (
        <>
          <div
            className="headline_regular_l text-content_inverse_primary"
            data-testid="paywall-title"
          >
            {paywallCopyOverrides?.title}
          </div>
          {paywallCopyOverrides?.subtitle && (
            <p className="body_medium_l text-content_inverse_primary">
              {paywallCopyOverrides.subtitle}
            </p>
          )}
        </>
      );
    }

    if (moduleName === IpoEnum[OpportunityTypeEnum.AI_PICK]) {
      return (
        <>
          <div className="headline_regular_l text-content_inverse_primary">
            <span className="mr-2">{I18n.t("This signal could earn you")}</span>
            <span>+{opportunity?.expected_return}%</span>
          </div>
          <p className="body_medium_l text-content_inverse_primary">
            {I18n.t("Ed already is.")}
          </p>
        </>
      );
    }

    if (moduleName === IpoEnum[OpportunityTypeEnum.EARNINGS]) {
      return (
        <>
          <div className="headline_regular_l text-content_inverse_primary leading-[1.3] break-words">
            <span>{I18n.t("Unlock")}</span>
            <NextImage
              src={opportunity?.asset?.logo || ""}
              className="inline-block w-8 h-8 rounded-full border border-border_solid overflow-hidden align-[-0.18em] mx-2"
              alt="EarningsIcon"
              width={24}
              height={24}
            />
            <span className="mr-2">{opportunity?.asset?.symbol}</span>
            {I18n.t("Earnings Forecast")}
          </div>
          <p className="body_medium_l text-content_inverse_primary">
            {I18n.t("Ed already is.")}
          </p>
        </>
      );
    }

    if (moduleName === IpoEnum[OpportunityTypeEnum.IPO]) {
      return (
        <>
          <div className="headline_regular_l text-content_inverse_primary">
            {I18n.t("Unlock")}
            <span className="mx-2">{opportunity?.asset?.symbol}</span>
            {I18n.t("IPO Strategies")}
          </div>
          <p className="body_medium_l text-content_inverse_primary">
            {I18n.t("Let Ed analyze the data for you")}
          </p>
        </>
      );
    }

    return (
      <>
        <div className="headline_regular_l text-content_inverse_primary">
          {I18n.t("You shouldn't have to watch the markets alone")}
        </div>
        <p className="body_medium_l text-content_inverse_primary">
          {I18n.t("Ed does that for you")}
        </p>
      </>
    );
  };

  const renderContextSubhero = () => {
    if (
      isCustomCopyMode ||
      moduleName === IpoEnum[OpportunityTypeEnum.AI_PICK]
    ) {
      return null;
    }
    if (
      moduleName === IpoEnum[OpportunityTypeEnum.EARNINGS] ||
      moduleName === IpoEnum[OpportunityTypeEnum.IPO]
    ) {
      return (
        <div className="body_medium_s text-content_inverse_primary/80">
          <span>{I18n.t("Return over the past 6 months for unlockers:")}</span>
          <span className="text-content_accent1_primary ml-1">
            {edgenReturnText}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="relative h-full w-full bg-background_solid_neutral_secondary"
      data-testid="onboarding-paywall-modal"
      data-figma-node="96138:32738"
      data-figma-node-pc={isPc ? "96344:111117" : undefined}
      data-figma-fallback={isPc ? "96344:111117" : undefined}
      {...gaTrackingAttrs("ob_funnel_paywall_view")}
    >
      <div
        className={cn(
          "absolute top-0 left-0 right-0 flex shrink-0 flex-col overflow-hidden",
          isPc ? "h-[495px]" : "h-65vh",
        )}
      >
        <div
          className={cn(
            "absolute inset-0",
            isPc ? "w-full h-[495px] translate-y-[-74px]" : "w-full h-65vh",
          )}
        >
          <AppImage
            src={heroBackgroundSrc}
            alt=""
            loading="eager"
            className={cn(
              "absolute inset-0 h-full w-full object-center object-cover",
            )}
          />
        </div>
        <div className="absolute inset-0 bg-[rgba(44,45,10,0.8)]" />

        <div
          className={cn(
            "relative z-10 flex items-center justify-center",
            isPc ? "" : "px-4 mt-safe py-4",
          )}
        >
          {!hideCloseButton && (
            <button
              type="button"
              aria-label="close"
              className={cn("absolute flex items-center justify-center", {
                "rounded-full bg-background_solid_neutral_primary left-4 p-2 top-50% -translate-y-50% size-10":
                  !isPc,
                "rounded-2 top-5 right-5 size-6": isPc,
              })}
              onClick={onClose}
            >
              <Icon
                data-openreplay-label="close"
                name="close"
                size={24}
                color={
                  isPc
                    ? ospThemes.colors.content_inverse_primary
                    : ospThemes.colors.content_primary
                }
              />
            </button>
          )}
          <Icon
            name="ed-logo"
            size={isPc ? 32 : 24}
            color={ospThemes.colors.content_inverse_primary}
            className={cn({
              "absolute top-8 left-8": isPc,
            })}
          />
        </div>
      </div>
      <div
        className={cn("absolute bottom-0 left-0 right-0 flex flex-col", {
          "gap-10": isPc,
          "gap-8": !isPc,
        })}
      >
        <div className="flex flex-col gap-6 px-4">
          <div className="flex flex-col gap-2">
            {renderHeroTitle()}
            {renderContextSubhero()}
          </div>

          <div className="flex flex-col gap-2">
            {featureItems.map((f) => (
              <div key={f.text} className="flex items-center gap-2">
                <Icon
                  name="check"
                  size={16}
                  className="shrink-0 text-content_inverse_primary"
                />
                <span className="body_l text-content_inverse_primary">
                  {f.text}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div
          className={cn(
            "flex flex-col rounded-t-3 bg-background_solid_neutral_primary",
            {
              "min-h-[418px]": !isPc && isBuyTrialNow,
              "min-h-[344px]": !isPc && !isBuyTrialNow,
            },
          )}
        >
          <div className="px-4 py-6">
            <EdCompleteAnnualCard annualPrice={annualDisplayPrice} />
          </div>
          <div className="flex-1"></div>
          {showSocialProof && (
            <div className="px-4 pb-6">
              <SocialProofItem />
            </div>
          )}
          <div className="bg-background_solid_neutral_secondary px-4 pb-safe">
            {!hideOfferCountdown && (
              <div
                data-testid="paywall-offer-countdown"
                className={cn(
                  "flex items-center justify-center gap-1 py-3 body_medium_s text-content_secondary",
                  isPc ? "mb-0" : "mb-3",
                )}
              >
                <span>
                  {isBuyTrialNow
                    ? I18n.t("Trial Offer Ends In")
                    : I18n.t("Offer Ends")}
                </span>
                <div className="flex items-center">
                  <div className="w-5 rounded-1 bg-background_auralv3_overlay100 text-center body_bold_s text-content_brand_primary">
                    {dailyCountdownTime.hh}
                  </div>
                  <span className="w-3 text-center body_s text-content_primary">
                    :
                  </span>
                  <div className="w-5 rounded-1 bg-background_auralv3_overlay100 text-center body_bold_s text-content_brand_primary">
                    {dailyCountdownTime.mm}
                  </div>
                  <span className="w-3 text-center body_s text-content_primary">
                    :
                  </span>
                  <div className="w-5 rounded-1 bg-background_auralv3_overlay100 text-center body_bold_s text-content_brand_primary">
                    {dailyCountdownTime.ss}
                  </div>
                </div>
              </div>
            )}

            <button
              aria-label="Paywall Modal Content"
              data-testid="paywall-primary-cta"
              data-openreplay-label={buildOpenReplayLabel(
                "Onboarding",
                "Paywall",
                "Trial",
                buttonText,
              )}
              id="btn-paywall-modal-content"
              className="button button_m emphasis_button md_button group h-11 w-full rounded-2 bg-background_accent4_overlay100 hover:bg-background_accent4_overlay50"
              disabled={isAppPayingUnlockLoading}
              data-loading={isAppPayingUnlockLoading}
              onClick={handleUnlockCardClick}
            >
              <span className="group-loading:hidden">
                {!isAppPayingUnlockLoading && buttonText}
              </span>
              <Icon
                name="ic_loading2"
                size={20}
                color={ospThemes.colors.content_inverse_primary}
                className="hidden animate-spin group-loading:block"
              />
            </button>

            <div className={cn("mt-2 mb-3")}>
              {isPc ? null : (
                <>
                  {showJustLookAroundButton ? (
                    <button
                      type="button"
                      aria-label="Paywall Just Look around"
                      data-openreplay-label={buildOpenReplayLabel(
                        "Onboarding",
                        "Paywall",
                        "Skip",
                        "Just Look around",
                      )}
                      id="btn-paywall-modal-just-look-around"
                      className="button_m text_button py-3 w-full text-content_tertiary"
                      onClick={handleJustLookAroundClick}
                    >
                      {I18n.t("Just Look around")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="button_m text_button py-3 w-full text-content_tertiary"
                      onClick={goToPricingPage}
                    >
                      {I18n.t("View all plans")}
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="pb-4 text-center body_s text-content_quaternary">
              {isBuyTrialNow && (
                <p>{I18n.t("No charges if canceled within 7 days")}</p>
              )}
              <p>
                {I18n.t("By subscribing, you agree to our")}{" "}
                <button
                  type="button"
                  onClick={handleTermsClick}
                  className="underline"
                >
                  {I18n.t("Terms of Use")}
                </button>{" "}
                {I18n.t("and")}{" "}
                <button
                  type="button"
                  onClick={handlePrivacyClick}
                  className="underline"
                >
                  {I18n.t("Privacy Policy")}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
